import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, ArrowRight, CheckCircle, Truck, Package, X, Calculator, MessageCircle, Mail, Star, Quote, Video, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { companyInfo } from '../data/mock';

// WhatsApp icon component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const testimonials = [
  {
    text: "Brilliant service! The team arrived on time and handled everything with care.",
    author: "Sarah T.",
    location: "Brighton"
  },
  {
    text: "Made our house move completely stress-free. Highly recommend!",
    author: "James M.",
    location: "Hove"
  },
  {
    text: "Professional, friendly and great value. Will definitely use again.",
    author: "Emma L.",
    location: "Worthing"
  },
  {
    text: "Fantastic team, nothing was too much trouble. 5 stars!",
    author: "David R.",
    location: "Lewes"
  }
];

const Hero = () => {
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const navigate = useNavigate();

  // Rotate testimonials (paused during react-snap prerender so the snapshot
  // always shows index 0 and hydration matches)
  useEffect(() => {
    if (navigator.userAgent === 'ReactSnap') return undefined;
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const highlights = [
    "Free No-Obligation Quotes",
    "Fully Insured Service",
    "Local & National Coverage"
  ];

  const handleQuoteClick = (e) => {
    e.preventDefault();
    setShowQuoteModal(true);
  };

  const closeModal = () => {
    setShowQuoteModal(false);
  };

  const handleCallClick = (e) => {
    e.preventDefault();
    setShowCallModal(true);
  };

  const closeCallModal = () => {
    setShowCallModal(false);
  };

  return (
    <section id="home" className="relative flex items-start pt-36 lg:pt-44 pb-20 overflow-hidden bg-[#0f1729]">
      {/* Decorative elements */}
      <div className="absolute top-40 right-0 w-96 h-96 bg-[#3B43AF]/30 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-0 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-br from-[#3B43AF]/10 to-transparent rounded-full blur-3xl"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 w-full">
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-12 items-start">
          {/* Content */}
          <div className="text-left">
            {/* Trust Badge - Above the fold */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="inline-flex items-center gap-2 bg-[#3B43AF]/20 text-[#7C83E0] px-4 py-2 rounded-full text-sm font-semibold badge-pill">
                <span className="w-2 h-2 bg-[#7C83E0] rounded-full animate-pulse"></span>
                We remove the stress from your move
              </div>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-[2.75rem] xl:text-5xl 2xl:text-6xl font-bold leading-[1.1] mb-6">
              <span className="block text-white whitespace-nowrap">
                Call us for <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400">a quote.</span>
              </span>
            </h1>
            
            <p className="text-xl text-gray-400 mb-8 leading-relaxed max-w-xl">
              No forms, no fuss — a quick call and we'll provide an instant quote.
            </p>

            {/* Highlights */}
            <div className="flex flex-wrap gap-4 mb-8">
              {highlights.map((item, index) => (
                <div key={index} className="flex items-center gap-2 text-gray-300">
                  <div className="w-5 h-5 bg-emerald-500/20 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                  </div>
                  <span className="font-medium">{item}</span>
                </div>
              ))}
            </div>

            {/* Google Reviews + Testimonial Tile — visible from lg up, sits under highlights */}
            <div className="hidden lg:block bg-white/[0.04] backdrop-blur-md rounded-2xl p-5 shadow-lg border border-white/10 max-w-lg">
              {/* Rotating Testimonial */}
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 bg-amber-400/15 rounded-full flex items-center justify-center flex-shrink-0">
                  <Quote className="w-5 h-5 text-amber-400" />
                </div>
                <div className="min-h-[60px]">
                  <p className="text-gray-300 italic mb-1 text-sm">
                    {`"${testimonials[currentTestimonial].text}"`}
                  </p>
                  <p className="text-xs font-semibold text-white">
                    {testimonials[currentTestimonial].author}
                    <span className="text-gray-400 font-normal">{` • ${testimonials[currentTestimonial].location}`}</span>
                  </p>
                </div>
              </div>

              {/* Dots indicator */}
              <div className="flex gap-1.5 mb-4 justify-center">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentTestimonial(i)}
                    className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                      i === currentTestimonial ? 'bg-[#7C83E0] w-4' : 'bg-white/20 hover:bg-white/40'
                    }`}
                    aria-label={`Testimonial ${i + 1}`}
                  />
                ))}
              </div>

              {/* Google Reviews Bar */}
              <div className="pt-4 border-t border-white/10 flex items-center justify-between gap-4">
                <a 
                  href="https://share.google/SPoCZSS7hLHds5fIk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 group bg-white/90 rounded-full px-3 py-1.5"
                >
                  <img 
                    src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png" 
                    alt="Google" 
                    className="h-5 w-auto"
                    width="92"
                    height="30"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <span className="font-bold text-stone-900 text-sm">5.0</span>
                </a>

                <img 
                  src="https://static.wixstatic.com/media/cd6915_71d02caadb4641a6a31836fe4eb4538b~mv2.webp" 
                  alt="Google Guaranteed" 
                  className="w-20 h-auto"
                  width="80"
                  height="80"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>

            {/* Mobile CTA - Visible on small screens only, right under highlights */}
            <div className="lg:hidden mb-6">
              <div className="flex flex-col gap-2">
                {/* Call Us Now */}
                <button 
                  onClick={handleCallClick}
                  className="bg-white/5 backdrop-blur-sm rounded-xl p-3 flex items-center gap-3 hover:bg-white/10 transition-all duration-300 border border-white/10 shadow-sm active:scale-[0.98]"
                  data-testid="hero-call-us-pill-mobile"
                >
                  <div className="w-10 h-10 bg-[#3B43AF] rounded-lg flex items-center justify-center flex-shrink-0 relative">
                    <Phone className="w-5 h-5 text-white" />
                    <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center">
                      <span className="absolute w-2.5 h-2.5 bg-emerald-400 rounded-full animate-ping opacity-50"></span>
                      <span className="relative w-1.5 h-1.5 bg-emerald-400 rounded-full"></span>
                    </span>
                  </div>
                  <div className="text-left">
                    <h4 className="font-heading font-semibold text-white text-sm">Call Us Now</h4>
                    <p className="text-xs text-gray-400">Phone or WhatsApp</p>
                  </div>
                </button>

                {/* Video Survey - opens Reloops widget (mobile) — HIDDEN on mobile per request (no longer connected to a form) */}
                <button 
                  onClick={() => {
                    if (window.Reloops && window.Reloops.open) {
                      window.Reloops.open();
                    } else if (window.ReloopsOpenJourney) {
                      window.ReloopsOpenJourney();
                    } else {
                      const btn = document.querySelector('[class*="reloops"], [id*="reloops"] button');
                      if (btn) btn.click();
                    }
                  }}
                  className="hidden bg-white/5 backdrop-blur-sm rounded-xl p-3 flex items-center gap-3 hover:bg-white/10 transition-all duration-300 border border-white/10 shadow-sm active:scale-[0.98]"
                >
                  <div className="w-10 h-10 bg-[#3B43AF] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Video className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <h4 className="font-heading font-semibold text-white text-sm">Video Survey</h4>
                    <p className="text-xs text-gray-400">Get an instant AI-powered quote</p>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Desktop Right Column - Hidden on mobile */}
          <div className="relative hidden lg:flex lg:flex-col">
            {/* iPhone AR Scan Mockup - Top of right column */}
            <div className="relative flex justify-center">
              {/* Glow behind phone */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#3B43AF]/30 via-amber-500/10 to-emerald-500/10 rounded-full blur-3xl scale-90"></div>
              <img
                src="/iphone-ar-scan.png"
                alt="HappyVan AI scanning a living room — bookshelf, sofa, coffee table and table lamp detected"
                className="relative w-full max-w-[380px] xl:max-w-[440px] h-auto drop-shadow-[0_25px_50px_rgba(0,0,0,0.5)] animate-slide-up"
                style={{
                  WebkitMaskImage: 'radial-gradient(ellipse 65% 70% at center, black 50%, transparent 85%)',
                  maskImage: 'radial-gradient(ellipse 65% 70% at center, black 50%, transparent 85%)',
                }}
                loading="eager"
                decoding="async"
                data-testid="hero-iphone-mockup"
              />
            </div>

            {/* Call Us Now pill — lower right, vertically aligned with the centre of the Google reviews tile */}
            <div className="mt-auto pt-16 xl:pt-24 flex justify-center">
              <div className="relative">
                {/* Pulsating green glow behind the pill */}
                <span
                  aria-hidden="true"
                  className="absolute inset-0 rounded-full bg-emerald-400/30 blur-2xl animate-pulse pointer-events-none scale-110"
                />
                <button 
                  onClick={handleCallClick}
                  className="relative bg-transparent border border-white/15 hover:border-white/30 backdrop-blur-sm rounded-full py-3 px-6 flex items-center justify-center gap-3 hover:bg-white/[0.04] transition-all duration-300 group cursor-pointer animate-slide-up hover:-translate-y-0.5"
                  data-testid="hero-call-us-pill"
                >
                  <div className="w-10 h-10 bg-emerald-400 rounded-full flex items-center justify-center group-hover:scale-105 transition-transform relative">
                    <Phone className="w-5 h-5 text-[#0f1729]" />
                    <span className="absolute -top-1 -right-1 flex items-center justify-center">
                      <span className="absolute w-2.5 h-2.5 bg-emerald-400 rounded-full animate-ping opacity-50"></span>
                      <span className="relative w-2 h-2 bg-emerald-400 rounded-full"></span>
                    </span>
                  </div>
                  <div className="text-left">
                    <h4 className="font-heading text-base font-semibold text-white">Call Us Now</h4>
                    <p className="text-xs text-gray-300">Phone or WhatsApp</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quote Option Modal */}
      {showQuoteModal && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div 
            className="bg-white rounded-3xl max-w-md w-full p-8 shadow-2xl transform transition-all animate-scale-in relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button 
              onClick={closeModal}
              className="absolute top-4 right-4 w-10 h-10 bg-stone-100 hover:bg-stone-200 rounded-full flex items-center justify-center transition-colors"
              aria-label="Close quote modal"
            >
              <X className="w-5 h-5 text-stone-600" />
            </button>

            {/* Modal content */}
            <div className="text-center mb-8">
              <h3 className="font-heading text-2xl font-bold text-stone-900 mb-2">Get Your Free Quote</h3>
              <p className="text-stone-600">Choose how you would like to receive your moving quote</p>
            </div>

            {/* Options */}
            <div className="space-y-4">
              {/* Call Now Button - Top Priority */}
              <a 
                href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                className="block w-full p-5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 rounded-2xl transition-all duration-300 group text-left shadow-lg hover:shadow-xl hover:-translate-y-1"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Phone className="w-7 h-7 text-blue-600" />
                  </div>
                  <div className="text-left flex-grow">
                    <h4 className="text-lg font-bold text-white">Call Now</h4>
                    <p className="text-blue-100 text-sm font-medium">{companyInfo.phone}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-white opacity-70 group-hover:opacity-100 transition-opacity" />
                </div>
              </a>

              <div className="relative flex items-center py-2">
                <div className="flex-grow border-t border-stone-200"></div>
                <span className="flex-shrink mx-4 text-stone-400 text-sm">or get a quote online</span>
                <div className="flex-grow border-t border-stone-200"></div>
              </div>

              {/* Option 1: Instant Calculator */}
              <button 
                onClick={() => {
                  closeModal();
                  navigate('/get-quote', { state: { selectedOption: 'calculator' } });
                }}
                className="block w-full p-5 bg-gradient-to-r from-amber-50 to-amber-100 hover:from-amber-100 hover:to-amber-200 border-2 border-amber-200 hover:border-amber-400 rounded-2xl transition-all duration-300 group text-left hover:-translate-y-1"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-amber-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg shadow-amber-500/25">
                    <Calculator className="w-7 h-7 text-white" />
                  </div>
                  <div className="text-left flex-grow">
                    <h4 className="text-lg font-bold text-stone-900">Instant Calculator</h4>
                    <p className="text-stone-600 text-sm"><strong className="text-amber-600">Instant</strong> estimation and booking</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-amber-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </button>

              {/* Option 2: WhatsApp Quote */}
              <button 
                onClick={() => {
                  closeModal();
                  navigate('/get-quote', { state: { selectedOption: 'whatsapp' } });
                }}
                className="block w-full p-5 bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 border-2 border-green-200 hover:border-green-500 rounded-2xl transition-all duration-300 group text-left hover:-translate-y-1"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-green-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg shadow-green-500/25">
                    <MessageCircle className="w-7 h-7 text-white" />
                  </div>
                  <div className="text-left flex-grow">
                    <h4 className="text-lg font-bold text-stone-900">WhatsApp Quote</h4>
                    <p className="text-stone-600 text-sm">Quick accurate quote in <strong className="text-green-600">1 Hour</strong></p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-green-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </button>

              {/* Option 3: Request a Call */}
              <button 
                onClick={() => {
                  closeModal();
                  navigate('/get-quote', { state: { selectedOption: 'contact' } });
                }}
                className="block w-full p-5 bg-gradient-to-r from-stone-50 to-stone-100 hover:from-stone-100 hover:to-stone-200 border-2 border-stone-200 hover:border-stone-400 rounded-2xl transition-all duration-300 group text-left hover:-translate-y-1"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-stone-700 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Mail className="w-7 h-7 text-white" />
                  </div>
                  <div className="text-left flex-grow">
                    <h4 className="text-lg font-bold text-stone-900">Request a Call</h4>
                    <p className="text-stone-600 text-sm">We call you and quote in <strong>1 day</strong></p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-stone-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </button>
            </div>

            {/* Footer note */}
            <p className="text-center text-sm text-stone-500 mt-6">
              All options are free with no obligation
            </p>
          </div>
        </div>
      )}

      {/* Call Us Modal - Phone or WhatsApp */}
      {showCallModal && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeCallModal}
        >
          <div 
            className="bg-white rounded-3xl max-w-sm w-full p-6 shadow-2xl transform transition-all animate-scale-in relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={closeCallModal}
              className="absolute top-4 right-4 p-2 rounded-full bg-stone-100 hover:bg-stone-200 transition-colors"
              aria-label="Close contact modal"
            >
              <X className="w-5 h-5 text-stone-600" />
            </button>

            <h3 className="text-xl font-heading font-bold text-center text-stone-900 mb-2">
              Contact Us
            </h3>
            <p className="text-stone-600 text-center mb-6 text-sm">
              Choose your preferred method
            </p>

            <div className="space-y-3">
              {/* Phone Option */}
              <a 
                href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                className="flex items-center gap-4 p-4 bg-[#3B43AF]/5 hover:bg-[#3B43AF]/10 border-2 border-[#3B43AF]/20 hover:border-[#3B43AF] rounded-2xl transition-all duration-300 group"
              >
                <div className="w-12 h-12 bg-[#3B43AF] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div className="flex-grow">
                  <h4 className="font-bold text-stone-900">Call Us</h4>
                  <p className="text-sm text-stone-600">{companyInfo.phone}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-[#3B43AF] opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>

              {/* WhatsApp Message */}
              <a 
                href={`https://wa.me/${companyInfo.whatsapp}?text=${encodeURIComponent('Hi HappyVan, I would like a quote for my move.')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-4 bg-green-50 hover:bg-green-100 border-2 border-green-200 hover:border-green-500 rounded-2xl transition-all duration-300 group"
                data-testid="whatsapp-message-link"
              >
                <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg shadow-green-500/25">
                  <WhatsAppIcon className="w-6 h-6 text-white" />
                </div>
                <div className="flex-grow">
                  <h4 className="font-bold text-stone-900">WhatsApp Message</h4>
                  <p className="text-sm text-stone-600">Send us a message</p>
                </div>
                <ArrowRight className="w-5 h-5 text-green-600 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>

              {/* WhatsApp Call */}
              <a 
                href={`https://wa.me/${companyInfo.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-4 bg-green-50 hover:bg-green-100 border-2 border-green-200 hover:border-green-500 rounded-2xl transition-all duration-300 group"
                data-testid="whatsapp-call-link"
              >
                <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg shadow-green-600/25">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div className="flex-grow">
                  <h4 className="font-bold text-stone-900">WhatsApp Call</h4>
                  <p className="text-sm text-stone-600">Voice call via WhatsApp</p>
                </div>
                <ArrowRight className="w-5 h-5 text-green-600 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;
